/**
 * The network graph's data: what the backend sends, and the nodes and links
 * the drawing is made from.
 *
 * The backend names organizations and sends everybody else as anonymous
 * numbers. People who belong to a single organization are not sent at all,
 * only counted, so the dots for them are made up here and stand for nobody in
 * particular.
 */

export type NetworkGraphData = {
  enabled: boolean;
  /** Named organizations, each with a count of people drawn only as dots */
  organizations?: { name: string; people: number }[];
  /** How many people are sent individually, numbered from zero */
  people?: number;
  /** [organization index, person index] */
  affiliations?: [number, number][];
  /** [person index, person index] */
  invitations?: [number, number][];
};

export type GraphNode = {
  id: string;
  type: "org" | "person";
  name?: string;
  x?: number;
  y?: number;
  degree?: number;
};

export type GraphLink = {
  source: string | GraphNode;
  target: string | GraphNode;
  type: string;
};

export type Graph = { nodes: GraphNode[]; links: GraphLink[] };

export type Scene = {
  focusId: string;
  nodes?: GraphNode[];
  links?: GraphLink[];
  activeNodeIds: Set<string>;
  activeLinkIds: Set<string>;
};

export type Point = { x: number; y: number };

/**
 * How many links out from the chosen organization count as its neighbourhood:
 * its own people, and the organizations it shares them with. One more would
 * bring in those organizations' people too, which crowds the picture.
 */
export const DEFAULT_HOPS = 2;
/** The furthest a neighbourhood is ever asked to reach. */
export const MAX_HOPS = 6;

/**
 * How many links out a neighbourhood reaches, from what somebody asked for.
 *
 * One link is an organization's own people, two the organizations it shares
 * them with, and so on outwards, so a larger number lights up more of the
 * network when an organization is chosen. Anything that is not a whole number
 * from 1 to MAX_HOPS is read as the default.
 */
export function hopsFrom(asked: string | null | undefined): number {
  if (!asked || !/^\d+$/.test(asked.trim())) return DEFAULT_HOPS;
  const hops = Number(asked.trim());
  return hops >= 1 && hops <= MAX_HOPS ? hops : DEFAULT_HOPS;
}

/** Turn the backend's compact form into the nodes and links the graph draws. */
export function makeNetworkGraph(data: NetworkGraphData): Graph {
  const nodes: GraphNode[] = [];
  const links: GraphLink[] = [];
  const organizations = data.organizations ?? [];

  organizations.forEach((organization, index) => {
    nodes.push({ id: `O${index}`, type: "org", name: organization.name });
  });
  for (let person = 0; person < (data.people ?? 0); person += 1) {
    nodes.push({ id: `P${person}`, type: "person" });
  }
  organizations.forEach((organization, index) => {
    for (let member = 0; member < organization.people; member += 1) {
      const id = `M${index}.${member}`;
      nodes.push({ id, type: "person" });
      links.push({ source: `O${index}`, target: id, type: "affiliation" });
    }
  });
  for (const [organization, person] of data.affiliations ?? []) {
    links.push({ source: `O${organization}`, target: `P${person}`, type: "affiliation" });
  }
  for (const [from, to] of data.invitations ?? []) {
    links.push({ source: `P${from}`, target: `P${to}`, type: "invite-claim" });
  }
  return { nodes, links };
}

const endpointId = (endpoint: string | GraphNode) =>
  typeof endpoint === "string" ? endpoint : endpoint.id;

export function focusNetworkGraph(graph: Graph, focusId: string, hops = DEFAULT_HOPS): Graph {
  if (!focusId) return graph;
  const adjacency = new Map<string, Set<string>>(graph.nodes.map((node) => [node.id, new Set<string>()]));
  for (const link of graph.links) {
    adjacency.get(endpointId(link.source))?.add(endpointId(link.target));
    adjacency.get(endpointId(link.target))?.add(endpointId(link.source));
  }
  const reached = new Set([focusId]);
  let frontier = new Set([focusId]);
  for (let step = 0; step < hops; step += 1) {
    const next = new Set<string>();
    for (const id of frontier) {
      for (const neighbor of adjacency.get(id) ?? []) {
        if (!reached.has(neighbor)) next.add(neighbor);
      }
    }
    for (const id of next) reached.add(id);
    frontier = next;
    if (!next.size) break;
  }
  return {
    nodes: graph.nodes.filter((node) => reached.has(node.id)),
    links: graph.links.filter(
      (link) => reached.has(endpointId(link.source)) && reached.has(endpointId(link.target))
    ),
  };
}

export function linkIdentity(link: GraphLink): string {
  return `${link.type}|${endpointId(link.source)}|${endpointId(link.target)}`;
}

export function focusScene(graph: Graph, focusId: string, hops = DEFAULT_HOPS): Scene {
  const validFocus = graph.nodes.some((node) => node.id === focusId && node.type === "org");
  const active = validFocus ? focusNetworkGraph(graph, focusId, hops) : graph;
  return {
    focusId: validFocus ? focusId : "",
    nodes: graph.nodes,
    links: graph.links,
    activeNodeIds: new Set(active.nodes.map((node) => node.id)),
    activeLinkIds: new Set(active.links.map(linkIdentity)),
  };
}

export function copyGraphForSimulation(graph: Graph): Graph {
  return {
    nodes: graph.nodes.map((node) => ({ ...node })),
    links: graph.links.map((link) => ({ ...link })),
  };
}
